import * as XLSX from 'xlsx';
import { classifyDataset } from './datasetClassifier';
import { profileColumns } from './columnProfiler';
import { assignColumns, columnChoices } from './columnAssigner';
import { extractSingleMeter } from './extractors/singleMeter';
import { describeMultiMeter } from './extractors/multiMeter';
import { extractDailyPeak } from './extractors/dailyPeak';
import { decodeTextFile, excelSerialToUtcWallClock, isCumulativeRegister, readCSVLines, splitRow } from '../fileParser';
import { validatePlausibility } from './plausibilityValidator';
import { checkRowShape, checkTimestampWindow, checkTimestampCollapse, checkWideDayLayout } from './invariants';
import type { InvariantViolation } from './invariants';
import type { SheetData, ManualPickerRequired, ParseResult, ParsedDataPoint, ColumnAssignment, ColumnChoices, ColumnKind, ColumnOverride, DatasetShape } from './types';
import { readingHoldsTogether, searchReadings } from './readingSearch';
import type { AuditArtifacts } from './auditPayload';
import { isGreenButtonXml, readGreenButton } from './greenButton';
import { applyLayout, detectLayout } from './layout';
import type { Layout } from './layout';
import { estimatedTimestampsFromRows } from './estimatedReadings';
import { estimatedRunsFrom } from './suspectPeaks';

export interface ParseOptions {
  /** A preparer's own choice of date/time and demand columns. See ColumnOverride. */
  columnOverride?: ColumnOverride;
  /** Reading instructions to apply instead of detecting them. See layout.ts. */
  layout?: Layout;
  /**
   * Who wrote `layout` and `columnOverride`: the preparer (the default, recorded
   * as their choice), the layout library, or Claude. See instructions.ts.
   */
  instructionsSource?: 'preparer' | 'library' | 'ai';
}

/**
 * Every sheet of a workbook as rows of plain values, with each date cell
 * converted to a fixed-UTC wall-clock Date.
 *
 * SheetJS's own `cellDates` option builds JS Dates in the runtime's local zone,
 * which made an xlsx upload's timestamps depend on the machine reading it,
 * left readings in the spring-forward hour with no representable local time,
 * and ended every one of them in .999Z from the serial's binary rounding. So the
 * workbook is read with number formats kept and dates left as serials; a cell
 * whose format is a date format is converted arithmetically. That is the same
 * fixed-offset rule `parseFlexibleTimestamp` applies to text, so a reading means
 * the same instant whether the utility sent it as CSV or as Excel.
 *
 * Row and column extent follow the sheet's declared range, blank cells are
 * null and blank rows are kept, matching what `sheet_to_json` produced before.
 */
export function readSpreadsheetSheets(content: ArrayBuffer): SheetData[] {
  const workbook = XLSX.read(content, { type: 'array', cellNF: true });
  const ssf = sheetJsFormats();
  const date1904 = workbook.Workbook?.WBProps?.date1904 === true;
  const sheets: SheetData[] = [];
  for (const name of workbook.SheetNames) {
    const sheet = workbook.Sheets[name];
    const ref = sheet?.['!ref'];
    if (!ref) {
      sheets.push({ name, rows: [] });
      continue;
    }
    const range = XLSX.utils.decode_range(ref);
    const rows: unknown[][] = [];
    for (let r = range.s.r; r <= range.e.r; r++) {
      const row: unknown[] = [];
      for (let c = range.s.c; c <= range.e.c; c++) {
        const cell = sheet[XLSX.utils.encode_cell({ r, c })] as XLSX.CellObject | undefined;
        row.push(cell ? spreadsheetCellValue(cell, date1904, ssf) : null);
      }
      rows.push(row);
    }
    sheets.push({ name, rows });
  }
  return sheets;
}

/**
 * SheetJS's number-format module. SheetJS is a CommonJS package: under Vite and
 * Vitest the namespace import carries every property of module.exports, but
 * under plain Node ESM (the tsx corpus sweep) only the statically detectable
 * exports are named and SSF is reachable only through `default`. Resolved once
 * per read, and refused loudly rather than letting a date cell fall through as
 * a bare serial number.
 */
function sheetJsFormats(): { is_date: (fmt: string) => boolean } {
  const ns = XLSX as unknown as { SSF?: typeof XLSX.SSF; default?: { SSF?: typeof XLSX.SSF } };
  const ssf = ns.SSF ?? ns.default?.SSF;
  if (!ssf || typeof ssf.is_date !== 'function') {
    throw new Error('Spreadsheet number formats are unavailable in this runtime, so date cells cannot be read.');
  }
  return ssf;
}

function spreadsheetCellValue(
  cell: XLSX.CellObject,
  date1904: boolean,
  ssf: { is_date: (fmt: string) => boolean }
): unknown {
  if (cell.t === 'n' && typeof cell.v === 'number' && typeof cell.z === 'string' && ssf.is_date(cell.z)) {
    return excelSerialToUtcWallClock(cell.v, date1904) ?? cell.v;
  }
  if (cell.t === 'd' && cell.v instanceof Date && !isNaN(cell.v.getTime())) {
    // A reader that already built a Date (some ODS paths) built it from the
    // cell's wall-clock in local time; read the same components back out and
    // re-key them to the fixed offset. Best effort: a spring-forward reading has
    // already been moved by the time it reaches us.
    const d = cell.v;
    return new Date(Date.UTC(
      d.getFullYear(), d.getMonth(), d.getDate(),
      d.getHours(), d.getMinutes(), Math.round(d.getSeconds() + d.getMilliseconds() / 1000)
    ));
  }
  return cell.v ?? null;
}

/**
 * The modular parser emits a few `metadata` fields beyond the shared
 * `ParseResult` shape: `demandUnit`/`headerRowIndex` (diagnostic echoes of the
 * column assignment and detected header row) and the Layer 3 fingerprint /
 * audit inputs that AMIDataInput forwards to parser_events and the auditor.
 * Declaring the superset here types what is actually produced without widening
 * the `ParseResult` contract declared in `../fileParser`.
 */
export interface AMIParseResult extends ParseResult {
  metadata?: NonNullable<ParseResult['metadata']> & {
    demandUnit?: ColumnAssignment['demandUnit'];
    headerRowIndex?: number;
    fingerprintHeader?: (string | null)[] | null;
    fingerprintColumnKinds?: ColumnKind[];
    auditArtifacts?: AuditArtifacts;
    /** Non-fatal Layer 0 violations. Fatal ones return an `errors` result instead. */
    invariantViolations?: InvariantViolation[];
    /** Set when the first reading failed and the reading search found another. */
    readingSearch?: { tried: number; passed: number; firstError: string | null };
    /** Who supplied the reading instructions. */
    instructionsSource?: 'preparer' | 'library' | 'ai';
    /** Raw rows from the header down, for buildLayoutSample. Never sent as-is. */
    layoutSampleSource?: { rows: unknown[][]; firstRowIndex: number };
    /** How the file laid out its readings, as applied. */
    readingLayout?: Layout;
    /** The columns this parse used, and what else a person could choose. */
    columnChoices?: ColumnChoices;
  };
}

/**
 * Frozen sentences of the AHJ disclosure. These must not be reworded: they are
 * the wording this product has put in front of authorities having jurisdiction
 * since launch, and are asserted verbatim in intervalDisclosure.test.ts.
 * Sentences 1 and 3 are interval-dependent and built by buildIntervalDisclosure().
 */
export const INTERVAL_DISCLOSURE_SENTENCE_2 =
  'NEC 220.87 specifies no minimum interval requirement.';
/**
 * Peak above which a reading is called out as inconsistent with a residential or
 * small commercial service. Exported with its message prefix because the warning
 * describes the file as parsed: if the preparer then excludes an outlier cluster
 * and the remaining peak is ordinary, the warning has become false and must be
 * dropped rather than left contradicting the calculated result in the report.
 */
export const PEAK_CEILING_KW = 500;
export const PEAK_CEILING_WARNING_PREFIX = 'Peak demand exceeds 500 kW';

export const INTERVAL_DISCLOSURE_SENTENCE_4 =
  'This limitation is disclosed for AHJ review.';

/**
 * Natural-language name for a sampling interval, used in both interval-dependent
 * sentences of the disclosure. Whole days read as daily/N-day, whole hours as
 * hourly/N-hour, anything else falls back to minutes.
 *   60 -> 'hourly'   120 -> '2-hour'   1440 -> 'daily'   2880 -> '2-day'   45 -> '45-minute'
 */
function describeInterval(intervalMinutes: number): string {
  if (intervalMinutes % 1440 === 0) {
    const days = intervalMinutes / 1440;
    return days === 1 ? 'daily' : `${days}-day`;
  }
  if (intervalMinutes % 60 === 0) {
    const hours = intervalMinutes / 60;
    return hours === 1 ? 'hourly' : `${hours}-hour`;
  }
  return `${intervalMinutes}-minute`;
}

/**
 * AHJ-facing disclosure for hourly-or-coarser source data. Sentences 1 and 3
 * name the detected interval so the statement stays factually true and
 * internally consistent at any granularity; sentences 2 and 4 are frozen.
 * The "relative to 15-minute data" comparison is deliberately fixed — 15 minutes
 * is the reference granularity, not a function of the detected interval.
 */
export function buildIntervalDisclosure(intervalMinutes: number): string {
  const label = describeInterval(intervalMinutes);
  // Sentence 3 opens the clause, so the label needs an initial capital.
  // Numeric labels ('2-hour') are unaffected by the uppercase.
  const adjectival = label.charAt(0).toUpperCase() + label.slice(1);
  return (
    `Data provided at ${label} intervals. ` +
    `${INTERVAL_DISCLOSURE_SENTENCE_2} ` +
    `${adjectival} interval data may understate short-duration demand peaks relative to 15-minute data. ` +
    `${INTERVAL_DISCLOSURE_SENTENCE_4}`
  );
}

/**
 * AHJ-facing disclosure for an export whose wall-clock timestamps repeat.
 *
 * A timestamp that appears twice inside a single file is the daylight-saving
 * fall-back signature: the local clock reads 01:00 twice and the export carries
 * no UTC offset to separate the two hours. `parseFlexibleTimestamp` maps naive
 * wall-clock strings to a fixed UTC instant precisely so the
 * runtime's own zone cannot distort them — which leaves both readings on one
 * instant, and mergeAMIDatasets then keeps the higher of the pair.
 *
 * We deliberately do NOT infer which reading belongs to which hour. Row order is
 * the only available signal and it is not evidence. Because the retained value
 * is always the higher one, the 220.87 peak is unaffected; what is lost is the
 * lower reading. Disclosed rather than silently dropped.
 */
export function buildRepeatedTimestampDisclosure(count: number): string {
  const readings = count === 1 ? 'reading' : 'readings';
  return (
    `${count} ${readings} in this file carry a timestamp that already appears earlier in the same file. ` +
    'This is characteristic of the daylight-saving time change, where the local clock repeats an hour and the ' +
    'export carries no time-zone offset to distinguish the two. The higher reading was retained at each ' +
    'repeated timestamp and the lower was not carried forward, so the calculated peak demand is unaffected. ' +
    `${INTERVAL_DISCLOSURE_SENTENCE_4}`
  );
}

/**
 * Refusal shaped like every other early return in this file. A fatal invariant
 * produces an `errors` entry, which the UI surfaces as a failed parse — the file
 * is not used for a calculation.
 */
function refuse(
  violation: InvariantViolation,
  detectedFormat: string,
  headerRow: (string | null)[] | null,
  extra: Record<string, unknown> = {}
): AMIParseResult {
  return {
    data: [],
    errors: [violation.message],
    warnings: [],
    detectedFormat,
    metadata: {
      intervalMinutes: null,
      powerSource: 'unknown',
      rowsIgnored: 0,
      rowsRejected: 0,
      dataQuality: [violation.message],
      plausibilityFlags: [],
      invariantViolations: [violation],
      fingerprintHeader: headerRow,
      ...extra,
    },
  } as unknown as AMIParseResult;
}

/** The first bytes of an XML document, whatever the file was named. */
function looksLikeXml(content: string | ArrayBuffer): boolean {
  const head = typeof content === 'string'
    ? content.slice(0, 512)
    : decodeTextFile(content.slice(0, Math.min(content.byteLength, 2048)));
  return /^\s*(?:\uFEFF)?<\?xml\b/.test(head) || /^\s*<(?:[\w.-]+:)?feed\b/.test(head);
}

/** The metadata every early refusal carries, so the screen can show the reason. */
function baseRefusalMetadata(message: string) {
  return {
    intervalMinutes: null,
    powerSource: 'unknown',
    rowsIgnored: 0,
    rowsRejected: 0,
    dataQuality: [message],
    plausibilityFlags: [],
    fingerprintHeader: null,
  };
}

function computeIntervalMinutes(points: ParsedDataPoint[]): number | null {
  if (points.length < 2) return null;
  const gaps: number[] = [];
  for (let i = 1; i < Math.min(points.length, 21); i++) {
    const diff = (new Date(points[i].timestamp).getTime() - new Date(points[i - 1].timestamp).getTime()) / 60000;
    if (diff > 0) gaps.push(diff);
  }
  if (gaps.length === 0) return null;
  const sorted = [...gaps].sort((a, b) => a - b);
  return Math.round(sorted[Math.floor(sorted.length / 2)]);
}

function computeSpanDays(points: ParsedDataPoint[]): number | null {
  if (points.length < 2) return null;
  let min = Infinity;
  let max = -Infinity;
  for (const p of points) {
    const t = new Date(p.timestamp).getTime();
    if (!Number.isFinite(t)) continue;
    if (t < min) min = t;
    if (t > max) max = t;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null;
  return (max - min) / (1000 * 60 * 60 * 24);
}

export async function parseAMIFile(
  content: string | ArrayBuffer,
  filename: string,
  options: ParseOptions = {}
): Promise<AMIParseResult> {
  const ext = filename.toLowerCase().split('.').pop() ?? '';
  const sheets: SheetData[] = [];
  // Set when the file was Green Button XML: what the reader left out and why,
  // carried onto the result as disclosures and as the generation signal.
  let greenButton: { reverseFlowPresent: boolean; readingCount: number; estimatedTimestamps: string[] } | null = null;

  // ── Read file into SheetData[] ────────────────────────────────────────────
  if (ext === 'xml' || (ext !== 'xlsx' && ext !== 'xls' && ext !== 'ods' && looksLikeXml(content))) {
    // Green Button XML (NAESB ESPI). The reader converts the feed into the same
    // two-column sheet every other upload becomes, so everything below runs on
    // it unchanged: no format-specific path past this point.
    const raw = typeof content === 'string' ? content : decodeTextFile(content);
    const text = raw.replace(/^\uFEFF/, '');
    if (!isGreenButtonXml(text)) {
      const message =
        'This XML file is not a Green Button interval export. If your utility offers Green Button ' +
        '"Download My Data", choose the interval (15-minute or hourly) usage export; otherwise upload the CSV or Excel file instead.';
      return { data: [], errors: [message], warnings: [], detectedFormat: 'XML (not Green Button)', metadata: baseRefusalMetadata(message) } as unknown as AMIParseResult;
    }
    const gb = readGreenButton(text);
    if (gb.error) {
      return { data: [], errors: [gb.error], warnings: [], detectedFormat: 'XML (Green Button)', metadata: baseRefusalMetadata(gb.error) } as unknown as AMIParseResult;
    }
    greenButton = {
      reverseFlowPresent: gb.reverseFlowPresent,
      readingCount: gb.readingCount,
      estimatedTimestamps: gb.estimatedTimestamps,
    };
    sheets.push({ name: 'Green Button', rows: gb.rows });
  } else if (ext === 'xlsx' || ext === 'xls' || ext === 'ods') {
    if (!(content instanceof ArrayBuffer)) {
      return { data: [], errors: [`${ext.toUpperCase()} file could not be read as binary data`], warnings: [], detectedFormat: `${ext.toUpperCase()} (error)` };
    }
    sheets.push(...readSpreadsheetSheets(content));
  } else {
    // Bytes are decoded here, not by the caller, so encoding detection lives in
    // one place and every caller — upload, function, test — gets the same answer.
    const raw = typeof content === 'string' ? content : decodeTextFile(content);
    const text = raw.replace(/^\uFEFF/, '');
    const { delimiter, lines } = readCSVLines(text);
    if (lines.length === 0) {
      return { data: [], errors: ['File is empty'], warnings: [], detectedFormat: 'CSV' };
    }
    const rows: unknown[][] = lines.map(l => splitRow(l, delimiter) as unknown[]);
    sheets.push({ name: 'CSV', rows });
  }

  // ── Classify dataset ──────────────────────────────────────────────────────
  const shape = classifyDataset(sheets);
  const chosenSheet = sheets.find(s => s.name === shape.sheet) ?? sheets[0];

  const headerRow = shape.headerRowIndex < chosenSheet.rows.length
    ? (chosenSheet.rows[shape.headerRowIndex] as unknown[]).map(c => c !== null && c !== undefined ? String(c) : null)
    : null;

  // ── Reading layout ────────────────────────────────────────────────────────
  // How the file lays out its readings: one timestamp column, a date and a time
  // of day in separate columns, or one row per day across the columns; and
  // whether rows for another service must be set aside. readSheet rewrites the
  // sheet into one timestamp column and one reading per row, and everything in
  // it runs on that unchanged. See layout.ts. Instructions handed in (the
  // preparer's separate time column, or a saved layout) outrank detection.
  const override = options.columnOverride;
  const detectedLayout = detectLayout(chosenSheet.rows, headerRow, shape.dataStartRow);
  const layout: Layout = options.layout
    ?? (override?.timeIndex !== undefined && override.timeIndex !== null
      ? { timestamp: { kind: 'date-time', dateColumn: override.timestampIndex, timeColumn: override.timeIndex }, rowFilter: detectedLayout.rowFilter }
      : detectedLayout);

  const source = options.instructionsSource ?? 'preparer';
  const ctx: SheetContext = { ext, sheets, shape, chosenSheet, headerRow, greenButton };
  const sampleFrom = layout.timestamp.kind === 'day-row' ? layout.timestamp.headerRowIndex : (layout.headerRowIndex ?? shape.headerRowIndex);
  const finish = (r: AMIParseResult): AMIParseResult => withLayoutSample(r, chosenSheet.rows, sampleFrom, headerRow, source);
  const first = finish(readSheet(ctx, layout, override, source === 'preparer' ? 'preparer' : 'trial'));

  // ── Try other readings when the first does not hold together ──────────────
  // Instructions someone gave are followed as given: a wrong pick is refused or
  // flagged, never quietly swapped. Otherwise a first reading that was refused,
  // needed the picker, or does not look like a meter history (uneven spacing,
  // far fewer rows than its dates imply) gets a second look across the file's
  // other plausible columns. See readingSearch.ts.
  if (override || options.layout) return first;
  if (readingHoldsTogether(first)) return first;
  return finish(searchReadings(ctx, detectedLayout, first, readSheet));
}

/**
 * Every result carries the rows a layout proposal would be made from, and the
 * file's own header row for the library key, whatever happened to the reading.
 * The sample is raw here; buildLayoutSample de-identifies it before it leaves
 * the browser. A reading made from Claude's instructions says so in the notes
 * that reach the report.
 */
function withLayoutSample(
  result: AMIParseResult,
  rows: unknown[][],
  firstRowIndex: number,
  headerRow: (string | null)[] | null,
  source: 'preparer' | 'library' | 'ai'
): AMIParseResult {
  const metadata = (result.metadata ?? {}) as NonNullable<AMIParseResult['metadata']>;
  const dataQuality = [...((metadata.dataQuality ?? []) as string[])];
  if (source === 'ai' && result.data.length > 0) {
    dataQuality.push(
      'TrueLoad had not seen this file layout before; which columns hold the dates and readings was identified ' +
      'with AI assistance. The readings themselves were computed by TrueLoad\'s deterministic parser and passed ' +
      'its standard checks.'
    );
  }
  return {
    ...result,
    metadata: {
      ...metadata,
      dataQuality,
      fingerprintHeader: metadata.fingerprintHeader ?? headerRow,
      instructionsSource: source,
      layoutSampleSource: { rows: rows.slice(firstRowIndex, firstRowIndex + 40), firstRowIndex },
    },
  };
}

/** What readSheet needs from the file, read once and shared by every trial. */
export interface SheetContext {
  ext: string;
  sheets: SheetData[];
  shape: DatasetShape;
  chosenSheet: SheetData;
  headerRow: (string | null)[] | null;
  greenButton: { reverseFlowPresent: boolean; readingCount: number; estimatedTimestamps: string[] } | null;
}

/**
 * One reading of the chosen sheet: apply the layout, profile and assign columns
 * (or take the ones given), extract, and run every invariant and plausibility
 * check. `overrideSource` says whose columns `override` carries: the preparer's
 * (recorded as theirs) or a trial of the reading search (recorded as detection).
 */
export function readSheet(
  ctx: SheetContext,
  layout: Layout,
  override: ColumnOverride | undefined,
  overrideSource: 'preparer' | 'trial'
): AMIParseResult {
  const { ext, sheets, shape, chosenSheet, headerRow, greenButton } = ctx;
  const dayRow = layout.timestamp.kind === 'day-row';
  const readKind = dayRow ? 'single-meter' : shape.kind;

  // A day-per-row header the reader could not turn into instructions (no date
  // column beside the readings) is refused by name: read as an ordinary file it
  // understates the peak a hundredfold.
  if (!dayRow) {
    const wideViolation = checkWideDayLayout(chosenSheet.rows);
    if (wideViolation) return refuse(wideViolation, readKind, headerRow);
  }

  // Instructions may name the header row themselves (a preamble the classifier
  // took for the header); otherwise the classifier's stands.
  const namedHeader = layout.timestamp.kind !== 'day-row' && layout.headerRowIndex !== undefined
    && layout.headerRowIndex >= 0 && layout.headerRowIndex < chosenSheet.rows.length
    ? layout.headerRowIndex
    : null;
  const layoutHeader = namedHeader === null
    ? headerRow
    : (chosenSheet.rows[namedHeader] as unknown[]).map(c => (c !== null && c !== undefined ? String(c) : null));
  const layoutStart = namedHeader === null ? shape.dataStartRow : namedHeader + 1;
  const applied = readKind === 'single-meter'
    ? applyLayout(chosenSheet.rows, layoutHeader, layoutStart, layout)
    : { rows: chosenSheet.rows, headerRow, dataStartRow: shape.dataStartRow, joined: null, rowsFilteredOut: 0, warnings: [] as string[] };
  const allRows = applied.rows;
  const dataStartRow = applied.dataStartRow;
  // The header the readings now sit under. The file's own header row stays the
  // fingerprint and the label on refusals.
  const readHeader = applied.headerRow;
  const dataRows = allRows.slice(dataStartRow);

  // ── Layer 0 invariants: structure ─────────────────────────────────────────
  // Runs before any column reasoning. A shifted row makes every downstream
  // decision meaningless, so there is nothing to be gained by profiling it.
  const invariantViolations: InvariantViolation[] = [];
  const isDelimited = !(ext === 'xlsx' || ext === 'xls' || ext === 'ods');
  if (isDelimited) {
    const shapeViolation = checkRowShape(readHeader, dataRows);
    if (shapeViolation?.fatal) {
      return refuse(shapeViolation, readKind, headerRow);
    }
    if (shapeViolation) invariantViolations.push(shapeViolation);
  }

  // ── Multiple meters — refuse before assigning columns ─────────────────────
  // Returned here rather than at the extractor branch below so the user gets the
  // specific reason. Column assignment on an interleaved sheet can succeed (it
  // did on the VEC export) or fail into a generic column-picker message; either
  // way "we found several meters" is the accurate and actionable one.
  if (readKind === 'multi-meter-interleaved') {
    const message = describeMultiMeter(shape);
    return {
      data: [],
      errors: [message],
      warnings: [],
      detectedFormat: readKind,
      metadata: {
        intervalMinutes: null,
        powerSource: 'unknown',
        rowsIgnored: 0,
        rowsRejected: 0,
        dataQuality: [message],
        plausibilityFlags: [],
        fingerprintHeader: headerRow,
      },
    } as unknown as ParseResult;
  }

  // ── Profile columns ───────────────────────────────────────────────────────
  const profiles = profileColumns(dataRows, readHeader);

  // A preparer's override must name two different columns that exist. Checked
  // here, once, so the assigner can trust it.
  if (override) {
    const has = (i: number) => profiles.some(p => p.index === i);
    if (!has(override.timestampIndex) || !has(override.demandIndex) || override.timestampIndex === override.demandIndex) {
      const message = 'The chosen date/time and demand columns must be two different columns of this file.';
      return {
        data: [],
        errors: [message],
        warnings: [],
        detectedFormat: readKind,
        metadata: {
          intervalMinutes: null,
          powerSource: 'unknown',
          rowsIgnored: 0,
          rowsRejected: 0,
          dataQuality: [message],
          plausibilityFlags: [],
          fingerprintHeader: headerRow,
          fingerprintColumnKinds: profiles.map(p => p.kind),
        },
      } as unknown as AMIParseResult;
    }
  }

  const assignmentResult = assignColumns(profiles, override, { trial: overrideSource === 'trial' });

  // ── ManualPickerRequired ──────────────────────────────────────────────────
  // The message travels in `errors` as well: a result with no readings must
  // always say why, whoever is reading it. The BED upload of 2026-09-08 reached
  // the screen with `errors: []`, and the screen had nothing to show but a
  // generic "No valid data found".
  if ('__manualPickerRequired' in assignmentResult) {
    const picker = assignmentResult as ManualPickerRequired;
    return {
      data: [],
      errors: [picker.message],
      warnings: [],
      detectedFormat: readKind,
      manualPickerRequired: picker,
      metadata: {
        intervalMinutes: null,
        powerSource: 'unknown',
        rowsIgnored: 0,
        rowsRejected: 0,
        dataQuality: [picker.message],
        plausibilityFlags: [],
        // Profiling ran even though assignment could not — expose the fingerprint
        // inputs so this parse still gets a format_fingerprint. No audit is fired
        // for a manual-required parse (there is no parser conclusion to audit).
        fingerprintHeader: headerRow,
        fingerprintColumnKinds: profiles.map(p => p.kind),
        telemetry: {
          sheet_count: sheets.length,
          chosen_sheet: shape.sheet,
          header_row_index: shape.headerRowIndex,
          header_confidence: null,
          timestamp_column: null,
          demand_column: null,
          demand_unit: null,
          interval_minutes: null,
          row_count: null,
          expected_row_count: null,
          span_days: null,
          peak_kw: null,
        },
      },
    } as unknown as ParseResult;
  }

  const assignment = assignmentResult;

  // ── Extract data points ───────────────────────────────────────────────────
  let extractResult: { dataPoints: ParsedDataPoint[]; warnings: string[]; metadata: Record<string, unknown> };

  if (readKind === 'single-meter') {
    extractResult = extractSingleMeter(allRows, assignment, dataStartRow);
    // 'multi-meter-interleaved' returned above, before column assignment.
  } else if (readKind === 'daily-peak-register') {
    extractDailyPeak(); // throws (never returns)
    throw new Error('unreachable');
  } else {
    throw new Error(`${readKind} extraction not yet implemented (Batch 2+)`);
  }

  const { dataPoints: extractedPoints, warnings, metadata } = extractResult;
  warnings.unshift(...applied.warnings);

  // ── Mixed row units with no detectable interval ───────────────────────────
  // The file states kWh on some rows and there is no interval to convert them
  // with. Reading them as kW would understate the peak by 60 / interval, and an
  // understated peak is the direction that wrongly frees capacity — so the file
  // is refused rather than parsed into a number nobody can trust.
  if (metadata.mixedRowUnitsUnconvertible === true) {
    const message =
      'This file states its unit on each row and includes kWh rows, but no reading interval ' +
      'could be determined from the timestamps, so those rows cannot be converted to kW. ' +
      'Please request an export with a consistent unit, or with complete interval timestamps.';
    return {
      data: [],
      errors: [message],
      warnings,
      detectedFormat: readKind,
      metadata: {
        intervalMinutes: null,
        powerSource: 'mixed_row_units',
        rowsIgnored: 0,
        rowsRejected: 0,
        dataQuality: [...warnings, message],
        plausibilityFlags: [],
        fingerprintHeader: headerRow,
      },
    } as unknown as ParseResult;
  }

  // ── Nothing extracted ─────────────────────────────────────────────────────
  // Columns were assigned but not one row yielded a date and a number together.
  // Left alone this would return `data: []` with no error, which the screen can
  // only render as a generic failure. Say what was tried.
  if (extractedPoints.length === 0) {
    const tsLabel = profiles.find(p => p.index === assignment.timestampIndex)?.header?.trim() || `column ${assignment.timestampIndex + 1}`;
    const demandLabel = profiles.find(p => p.index === assignment.demandIndex)?.header?.trim() || `column ${assignment.demandIndex + 1}`;
    const message =
      `TrueLoad identified '${tsLabel}' as the date/time column and '${demandLabel}' as the demand column, ` +
      'but could not read a single complete reading from them. Please check that each row carries a date and ' +
      'time together with a numeric reading, or email the file to support@trueloadapp.com.';
    return {
      data: [],
      errors: [message],
      warnings,
      detectedFormat: readKind,
      metadata: {
        intervalMinutes: null,
        powerSource: 'unknown',
        rowsIgnored: 0,
        rowsRejected: dataRows.length,
        dataQuality: [...warnings, message],
        plausibilityFlags: [],
        fingerprintHeader: headerRow,
        fingerprintColumnKinds: profiles.map(p => p.kind),
      },
    } as unknown as AMIParseResult;
  }

  // ── Layer 0 invariants: timestamps ────────────────────────────────────────
  // Deliberately ahead of the data-quality disclosures below. The original
  // failure was caught by the repeated-timestamp check and then narrated as a
  // daylight-saving artifact; an invariant that a disclosure can talk its way
  // past is not an invariant.
  const windowCheck = checkTimestampWindow(extractedPoints);
  if (windowCheck.violation?.fatal) {
    return refuse(windowCheck.violation, readKind, headerRow);
  }
  if (windowCheck.violation) invariantViolations.push(windowCheck.violation);
  const dataPoints = windowCheck.kept;

  const collapseViolation = checkTimestampCollapse(dataPoints);
  if (collapseViolation?.fatal) {
    return refuse(collapseViolation, readKind, headerRow);
  }
  if (collapseViolation) invariantViolations.push(collapseViolation);

  // ── Telemetry scalars (observational — computed for every downstream path) ─
  const peakKw = dataPoints.length > 0 ? Math.max(...dataPoints.map(p => p.kw)) : 0;
  const peakTimestamp = dataPoints.length > 0
    ? (dataPoints.find(p => p.kw === peakKw)?.timestamp ?? null)
    : null;
  const intervalMinutes = computeIntervalMinutes(dataPoints);
  const spanDays = computeSpanDays(dataPoints);
  const expectedRowCount = (intervalMinutes && intervalMinutes > 0 && spanDays && spanDays > 0)
    ? Math.round(spanDays * (1440 / intervalMinutes))
    : null;
  const powerSource = assignment.mixedRowUnits === true
    ? 'mixed_row_units' as const
    : assignment.demandUnit === 'kWh' ? 'derived_kwh' as const : 'kw' as const;
  const telemetry = {
    sheet_count: sheets.length,
    chosen_sheet: shape.sheet,
    header_row_index: shape.headerRowIndex,
    // header_confidence: numeric confidence of the chosen demand column (see report).
    header_confidence: profiles.find(p => p.index === assignment.demandIndex)?.confidence ?? null,
    timestamp_column: applied.joined && applied.joined.column === assignment.timestampIndex
      ? applied.joined.label
      : profiles.find(p => p.index === assignment.timestampIndex)?.header ?? null,
    demand_column: profiles.find(p => p.index === assignment.demandIndex)?.header ?? null,
    demand_unit: assignment.demandUnit,
    interval_minutes: intervalMinutes,
    row_count: dataPoints.length,
    expected_row_count: expectedRowCount,
    span_days: spanDays,
    peak_kw: dataPoints.length > 0 ? peakKw : null,
  };

  // ── Validation ────────────────────────────────────────────────────────────
  if (isCumulativeRegister(dataPoints.map(p => p.kw))) {
    return {
      data: [],
      errors: [
        'The selected demand column appears to contain cumulative register readings rather than interval demand values. ' +
        'Please contact your utility for interval (demand) data, not cumulative energy totals.',
      ],
      warnings,
      detectedFormat: readKind,
      metadata: {
        intervalMinutes,
        powerSource,
        rowsIgnored: 0,
        rowsRejected: 0,
        dataQuality: [...warnings],
        plausibilityFlags: [],
        telemetry,
      },
    };
  }

  if (peakKw > PEAK_CEILING_KW) {
    // The warning names the ceiling, not the peak: it shows on the Existing
    // Load tab before unlock, and the measured peak is paid content.
    warnings.push(
      `${PEAK_CEILING_WARNING_PREFIX}. ` +
      'This is not consistent with a residential or small commercial service.'
    );
  }

  if (dataPoints.length > 1) {
    if (dataPoints.every(p => p.kw === 0)) {
      warnings.push('All demand values are zero. This may indicate an incorrect column was selected.');
    } else if (dataPoints.every(p => p.kw === dataPoints[0].kw)) {
      warnings.push(`All demand values are identical (${dataPoints[0].kw} ${assignment.demandUnit}). This may indicate the wrong column was selected.`);
    }
  }

  // Hourly-or-coarser source data carries an AHJ disclosure in report output.
  // Kept out of `warnings` so it reaches
  // ami_data_quality_notes without surfacing as an upload-time warning.
  const dataQuality = [...warnings, ...invariantViolations.map(v => v.message)];
  if (intervalMinutes !== null && intervalMinutes >= 60) {
    dataQuality.push(buildIntervalDisclosure(intervalMinutes));
  }
  // Per-row units: the report says which units the file named and how many rows
  // carried each, because a mixed-unit export is unusual enough that an AHJ
  // should see it stated rather than have to trust that it was handled.
  if (metadata.mixedRowUnits === true) {
    const counts = (metadata.rowUnitCounts ?? {}) as Record<string, number>;
    const parts = Object.entries(counts).map(([unit, n]) => `${n} ${unit}`);
    const unlabeled = (metadata.unlabeledUnitRows ?? 0) as number;
    dataQuality.push(
      `This export states its unit on each row (${parts.join(', ')}). Each row was converted ` +
      `to kW using its own stated unit` +
      (intervalMinutes ? ` and the detected ${intervalMinutes}-minute interval` : '') + '.' +
      (unlabeled > 0
        ? ` ${unlabeled} row(s) named no unit and were read as ${assignment.demandUnit}.`
        : '')
    );
  }
  // Green Button: say what the reader did. Values were converted from the
  // feed's own unit through each reading's duration, timestamps are true
  // instants written as UTC, and an export channel, if present, was left out.
  if (greenButton) {
    dataQuality.push(
      'Source file was Green Button XML (NAESB ESPI). Readings were converted to kW from the file\'s stated ' +
      'unit using each reading\'s own interval; timestamps are the file\'s epoch instants, shown in UTC.'
    );
    if (greenButton.reverseFlowPresent) {
      dataQuality.push(
        'The Green Button file also carried a reverse-flow (export) channel, which was not included: ' +
        'the demand above is the delivered energy only. See the gross-versus-net note in the methodology.'
      );
    }
  }

  // Repeated wall-clock timestamps inside one export — the DST fall-back hour.
  // Counted here rather than in an extractor so every dataset shape is covered.
  const repeatedTimestampCount =
    dataPoints.length - new Set(dataPoints.map(p => p.timestamp)).size;
  if (repeatedTimestampCount > 0) {
    dataQuality.push(buildRepeatedTimestampDisclosure(repeatedTimestampCount));
  }

  // Readings the source itself marks as estimated: Green Button ReadingQuality,
  // or a column named for it. Observational, like everything below.
  const estimatedRuns = estimatedRunsFrom(
    dataPoints,
    greenButton
      ? greenButton.estimatedTimestamps
      : estimatedTimestampsFromRows(allRows, readHeader, assignment, dataStartRow),
    intervalMinutes
  );

  // ── Layer 2 plausibility (observational; never changes parsing decisions) ──
  const plausibilityFlags = validatePlausibility({
    dataPoints,
    intervalMinutes,
    profiles,
    shape,
    assignment,
    sheetCount: sheets.length,
    estimatedRuns,
  });

  // ── Layer 3 audit artifacts (observational; consumed by auditPayload.ts) ──
  // Exposes already-computed outputs only — no parsing decision is made here.
  // `dataRows` starts AT/AFTER the detected header (preamble excluded by slice).
  const auditArtifacts = {
    headerRow: readHeader,
    columns: profiles.map(p => ({ index: p.index, header: p.header, kind: p.kind, stats: p.stats })),
    idLikeIndices: profiles.filter(p => p.kind === 'id-like').map(p => p.index),
    dataRows,
    demandIndex: assignment.demandIndex,
    sheetInventory: sheets.map(s => ({
      name: s.name,
      rowCount: s.rows.length,
      colCount: s.rows.reduce((m, r) => Math.max(m, (r as unknown[]).length), 0),
    })),
    conclusion: {
      chosenSheet: shape.sheet,
      headerRowIndex: shape.headerRowIndex,
      timestampColumn: telemetry.timestamp_column,
      demandColumn: telemetry.demand_column,
      unit: assignment.demandUnit,
      intervalMinutes,
      rowCount: dataPoints.length,
      spanDays,
      peakKw: dataPoints.length > 0 ? peakKw : null,
      peakTimestamp,
    },
    plausibilityFlags,
  };

  return {
    data: dataPoints,
    errors: [],
    warnings,
    detectedFormat: `${ext.toUpperCase() || 'CSV'} (sheet: ${shape.sheet})`,
    columnSelectionLowConfidence: assignment.confidence === 'low',
    generationColumnDetected: assignment.generationIndex !== null || greenButton?.reverseFlowPresent === true,
    grossPeakKW: undefined,
    metadata: {
      intervalMinutes,
      powerSource,
      demandUnit: assignment.demandUnit,
      headerRowIndex: shape.headerRowIndex,
      rowsIgnored: 0,
      rowsRejected: 0,
      dataQuality,
      columnSelectionLowConfidence: assignment.confidence === 'low',
      generationColumnDetected: assignment.generationIndex !== null || greenButton?.reverseFlowPresent === true,
      grossPeakKW: undefined,
      plausibilityFlags,
      // Runs of utility-estimated readings; the upload stores them on the project.
      estimatedRuns,
      invariantViolations,
      telemetry,
      // Layer 3: fingerprint inputs (every profiled parse) + de-id audit source.
      fingerprintHeader: headerRow,
      fingerprintColumnKinds: profiles.map(p => p.kind),
      auditArtifacts,
      readingLayout: layout,
      columnChoices: columnChoices(profiles, {
        timestampIndex: assignment.timestampIndex,
        demandIndex: assignment.demandIndex,
        timeIndex: layout.timestamp.kind === 'date-time' && applied.joined?.column === assignment.timestampIndex
          ? layout.timestamp.timeColumn
          : null,
      }),
      ...metadata,
    },
  };
}
